import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import Dialog from "components/dialog";
import {
  EMPTY_NEW_PATIENT_SEED,
  toPhoneDigits
} from "components/patient-search/new-patient-seed.utils";
import PatientForm from "features/patients/patient-form";
import type { Patient } from "services/types/patients.type";

import { seedFromCallerId } from "./caller-identity.util";
import type { AttachRecovery } from "./use-unknown-caller-actions.hook";

type QuickAddPatientDialogProps = {
  remoteNumber: string;
  fromName: string | null;
  recovery: AttachRecovery | null;
  isAttaching: boolean;
  onClose: () => void;
  // Resolves true when the call was attached, false when the patient exists
  // but the attach failed (the host then shows `recovery`).
  onCreated: (patientId: number, patientName: string) => Promise<boolean>;
  onRetryAttach: () => Promise<void>;
  // Root styles for the recovery dialog (the softphone lifts its dialogs over
  // the panel). The New Patient form is `PatientForm`'s own dialog.
  sx?: SxProps;
};

// The New Patient form prefilled from the caller: phone from the line, name
// from the carrier's caller ID (conservatively split — see seedFromCallerId).
// Saving creates the patient AND attaches the call; if only the first half
// succeeds the dialog switches to a recovery view rather than offering to
// create the patient again.
export default function QuickAddPatientDialog({
  remoteNumber,
  fromName,
  recovery,
  isAttaching,
  onClose,
  onCreated,
  onRetryAttach,
  sx
}: QuickAddPatientDialogProps) {
  const newPatient = useMemo(
    () => ({
      ...EMPTY_NEW_PATIENT_SEED,
      ...seedFromCallerId(fromName),
      phone: toPhoneDigits(remoteNumber)
    }),
    [fromName, remoteNumber]
  );

  if (recovery) {
    return (
      <Dialog
        title="Patient created"
        open
        onClose={onClose}
        maxWidth="xs"
        disableDismissOnClickAway
        sx={sx}
      >
        <Typography variant="body2" data-testid="quick-add-recovery">
          {recovery.patientName} was added, but this call could not be attached
          to them. Retry the attachment, or open the patient and attach the call
          from the Calls page later.
        </Typography>
        <DialogActions sx={{ px: 0, pb: 0, mt: 2 }}>
          <Button
            component={RouterLink}
            to={`/patients/${recovery.createdPatientId}`}
            onClick={onClose}
          >
            Open patient
          </Button>
          <LoadingButton
            variant="contained"
            loading={isAttaching}
            onClick={onRetryAttach}
            data-testid="quick-add-retry-attach"
          >
            Retry attach
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <PatientForm
      isOpen
      newPatient={newPatient}
      onCancel={onClose}
      onSave={async (patient: Patient) => {
        const name = `${patient.preferred_name || patient.first_name} ${
          patient.last_name
        }`.trim();
        const attached = await onCreated(patient.id, name);
        if (attached) onClose();
      }}
    />
  );
}
